import { Deserializable } from '../../protocols/deserializable';
import { EmployerStatusTypeEnum } from '../../employers/enum/employer-status-type.enum';
import { EmployerStatusType } from '../../employers/dto/employer-status-type';
import { EmployerTypeIdEnum } from '../../employers/enum/employer-type-id.enum';
import { EmployerType } from '../../employers/dto/employer-type';
import { GoverningJurisdictionTypeId } from '../../enum/shared/governing-jurisdiction-type-id';
import { GoverningJurisdictionType } from '../../employers/employer-history/types/governing-jurisdiction-type';
import { BaseType } from '../../base/base-type';
import { Address } from '../../users/dto/address';
import { EmployerContact } from '../../employers/dto/employer-contact';
import { EmployerPlanType } from '../../employers/plans/employer-plan-type';
import { DesignatedUnitContributionRateBasisType } from '../../employers/plans/designated-unit-contribution-rate-basis-type';
import { ContributionRateCalculationType } from '../../employers/enum/contribution-rate-calculation-type';
import { DesignatedUnitContributionRateCalculationType } from '../../employers/plans/designated-unit-contribution-rate-calculation-type';
import { DesignatedUnitLessThanFullTimeParticipationType } from '../../employers/plans/designated-unit-less-than-full-time-participation-type';
import { SubmissionAttachment } from '../submission-attachment';
import { EmployerSubmissionLogo } from './employer-submission-logo';

export class NewEmployerSubmissionData implements Deserializable {
  public name!: string;
  public shortName!: string;
  public organizationCreationDate!: string;
  public entranceDate!: string;
  public craPayrollRemittanceAccountNumber!: string;
  public statusId!: EmployerStatusTypeEnum;
  public status!: EmployerStatusType;
  public typeId!: EmployerTypeIdEnum;
  public type!: EmployerType;
  public governingJurisdictionId!: GoverningJurisdictionTypeId;
  public governingJurisdiction!: GoverningJurisdictionType;
  public votingRegionId!: number;
  public votingRegion!: BaseType;
  public headOfficeAddress!: Address;
  public mailingAddress!: Address;
  public logo!: SubmissionAttachment;
  public employerLogoUpload!: EmployerSubmissionLogo | null; // only present when submission is approved on create
  public plan!: NewEmployerSubmissionPlan;
  public contacts!: EmployerContact[];

  public onDeserialize(): void {
    if (!!this.status) {
      this.status = window.injector.Deserialize.instanceOf(EmployerStatusType, this.status);
    }
    if (!!this.type) {
      this.type = window.injector.Deserialize.instanceOf(EmployerType, this.type);
    }
    if (!!this.governingJurisdiction) {
      this.governingJurisdiction = window.injector.Deserialize.instanceOf(
        GoverningJurisdictionType,
        this.governingJurisdiction
      );
    }
    if (!!this.votingRegion) {
      this.votingRegion = window.injector.Deserialize.instanceOf(BaseType, this.votingRegion);
    }
    if (!!this.headOfficeAddress) {
      this.headOfficeAddress = window.injector.Deserialize.instanceOf(Address, this.headOfficeAddress);
    }
    if (!!this.mailingAddress) {
      this.mailingAddress = window.injector.Deserialize.instanceOf(Address, this.mailingAddress);
    }
    if (!!this.logo) {
      this.logo = window.injector.Deserialize.instanceOf(SubmissionAttachment, this.logo);
    }
    if (!!this.employerLogoUpload) {
      this.employerLogoUpload = window.injector.Deserialize.instanceOf(EmployerSubmissionLogo, this.employerLogoUpload);
    }
    if (!!this.plan) {
      this.plan = window.injector.Deserialize.instanceOf(NewEmployerSubmissionPlan, this.plan);
    }
    if (!!this.contacts) {
      this.contacts = window.injector.Deserialize.arrayOf(EmployerContact, this.contacts);
    }
  }
}

export class NewEmployerSubmissionPlan implements Deserializable {
  public employerPlanTypeId!: number;
  public employerPlanType!: EmployerPlanType;
  public planEstablishmentDate!: string;
  public designatedUnits!: NewEmployerSubmissionDesignatedUnit[];

  public onDeserialize(): void {
    if (!!this.designatedUnits) {
      this.designatedUnits = window.injector.Deserialize.arrayOf(
        NewEmployerSubmissionDesignatedUnit,
        this.designatedUnits
      );
    }
  }
}

export class NewEmployerSubmissionDesignatedUnit implements Deserializable {
  public id!: string;
  public designatedUnitId!: number;
  public employerPlanId!: number;
  public designatedUnitTypeId!: number;
  public designatedUnitType!: BaseType;
  public remittanceFrequencyTypeId!: number;
  public remittanceFrequencyType!: BaseType;
  public contributionRateBasisTypeId!: number;
  public contributionRateBasisType!: DesignatedUnitContributionRateBasisType;
  public matchedContributionRateTypeId!: ContributionRateCalculationType;
  public matchedContributionRateType!: DesignatedUnitContributionRateCalculationType;
  public matchedContributionRatePercentage!: number | null;
  public matchedContributionRateRangeFirstPercentage!: number | null;
  public matchedContributionRateRangeSecondPercentage!: number | null;
  public unmatchedContributionRateTypeId!: ContributionRateCalculationType;
  public unmatchedContributionRateType!: DesignatedUnitContributionRateCalculationType;
  public unmatchedContributionRatePercentage!: number | null;
  public unmatchedContributionRateRangeFirstPercentage!: number | null;
  public unmatchedContributionRateRangeSecondPercentage!: number | null;
  public fullTimeWaitingPeriod!: number;
  public lessThanFullTimeParticipationTypeId!: number;
  public lessThanFullTimeParticipationType!: DesignatedUnitLessThanFullTimeParticipationType;
  public lessThanFullTimeWaitingPeriod!: number;
  public startDate!: string;
  public endDate!: string | null;
  public clientUnitId!: string;
  public unitName!: string;

  public onDeserialize(): void {
    if (!!this.contributionRateBasisType) {
      this.contributionRateBasisType = window.injector.Deserialize?.instanceOf(
        DesignatedUnitContributionRateBasisType,
        this.contributionRateBasisType
      );
    }
    if (!!this.matchedContributionRateType) {
      this.matchedContributionRateType = window.injector.Deserialize?.instanceOf(
        DesignatedUnitContributionRateCalculationType,
        this.matchedContributionRateType
      );
    }
    if (!!this.unmatchedContributionRateType) {
      this.unmatchedContributionRateType = window.injector.Deserialize?.instanceOf(
        DesignatedUnitContributionRateCalculationType,
        this.unmatchedContributionRateType
      );
    }
    if (!!this.lessThanFullTimeParticipationType) {
      this.lessThanFullTimeParticipationType = window.injector.Deserialize?.instanceOf(
        DesignatedUnitLessThanFullTimeParticipationType,
        this.lessThanFullTimeParticipationType
      );
    }
    if (!!this.designatedUnitType) {
      this.designatedUnitType = window.injector.Deserialize?.instanceOf(BaseType, this.designatedUnitType);
    }
    if (!!this.remittanceFrequencyType) {
      this.remittanceFrequencyType = window.injector.Deserialize?.instanceOf(BaseType, this.remittanceFrequencyType);
    }
  }
}
